'use client';

import { usePagination } from '@liquorice/next-search';
import Btn, { BtnProps } from '../ui/Btn';
import Flex from '../ui/Flex';

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const PaginationBtn = ({ onClick, ...props }: BtnProps) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    scrollToTop();
    onClick?.(event);
  };

  return (
    <Btn
      size="small"
      variant="filled"
      color="white"
      rounded="large"
      onClick={handleClick}
      {...props}
    />
  );
};

type PaginationNumberProps = BtnProps<
  'button',
  {
    number: number;
    active?: boolean;
    onClick: () => void;
    currentPage: number;
  }
>;

const PaginationNumber = ({
  number,
  onClick,
  currentPage,
  active,
  ...props
}: PaginationNumberProps) => {
  const handleClick = () => {
    scrollToTop();
    onClick();
  };

  return (
    <Btn
      variant="text"
      rounded="large"
      color="black"
      underline={active ? 'always' : 'none'}
      onClick={handleClick}
      {...props}>
      {number}
    </Btn>
  );
};

const Pagination = () => {
  const { page, totalPages, setPage, nextPage, prevPage, firstPage, lastPage } = usePagination();
  const pagesArray = Array.from({ length: totalPages }, (_, i) => i + 1).filter(
    (i) => i > page - 3 && i < page + 3 && i !== 1 && i !== totalPages
  );

  if (totalPages === 1) return null;

  return (
    <Flex direction="row" alignItems="center" justifyContent="center" rowGap="2xs" columnGap="2xs">
      <PaginationBtn onClick={prevPage} disabled={page === 1} startIcon="chevronLeft" />
      <PaginationNumber number={1} onClick={firstPage} currentPage={page} active={page === 1} />
      {page > 4 && <span>&hellip;</span>}
      {pagesArray.map((i) => (
        <PaginationNumber
          key={i}
          number={i}
          onClick={() => setPage(i)}
          currentPage={page}
          active={page === i}
        />
      ))}
      {page < totalPages - 3 && <span>&hellip;</span>}
      {totalPages !== 1 && (
        <PaginationNumber
          number={totalPages}
          onClick={lastPage}
          currentPage={page}
          active={page === totalPages}
        />
      )}
      <PaginationBtn onClick={nextPage} disabled={page === totalPages} endIcon="chevronRight" />
    </Flex>
  );
};

export default Pagination;
